import { logger } from "@/lib/logger";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Search,
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Trash2,
  ArrowRight,
  Info,
} from "lucide-react";
import { ClaimTrialDialog } from "./ClaimTrialDialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { extractDocumentText } from "@/lib/regulatory/elsa/extractDocumentText";
import { extractTrialMetadataInChunks } from "@/lib/trial-site/chunkedTrialExtraction";
import { persistTrialExtraction } from "@/lib/trial-site/persistTrialExtraction";

interface AddActiveTrialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteOrgId: string;
  trialSiteId?: string;
  onTrialCreated?: (trialId: string, trialTitle: string) => void;
}

type Step = "trial-info" | "upload-etmf" | "done";
type DocStatus = "queued" | "uploading" | "filed" | "failed";

interface PendingDoc {
  file: File;
  size: number;
  doc_type_guess: string;
  status: DocStatus;
  error?: string;
}

/** DIA TMF zone mapping — mirrors CreateTrialDialog. */
const TMF_ZONE_MAP: Record<string, { zone: string; section: string; artifact: string; category: string }> = {
  protocol:              { zone: "02", section: "02.01", artifact: "Protocol",                     category: "regulatory" },
  investigator_brochure: { zone: "02", section: "02.03", artifact: "Investigator's Brochure",      category: "regulatory" },
  icf:                   { zone: "02", section: "02.04", artifact: "Informed Consent Form",        category: "regulatory" },
  lab_manual:            { zone: "03", section: "03.02", artifact: "Central Laboratory Manual",    category: "operational" },
  ip_manual:             { zone: "04", section: "04.01", artifact: "IP / Pharmacy Manual",         category: "operational" },
  other:                 { zone: "08", section: "08.01", artifact: "Miscellaneous Study Document", category: "other" },
};

function guessDocType(name: string): string {
  const n = name.toLowerCase().replace(/[+_-]+/g, " ");
  if (n.includes("protocol")) return "protocol";
  if (n.includes("brochure") || n.includes(" ib ") || n.endsWith("ib.pdf")) return "investigator_brochure";
  if (n.includes("icf") || n.includes("consent")) return "icf";
  if (n.includes("lab")) return "lab_manual";
  if (n.includes("pharmacy") || n.includes("ip manual")) return "ip_manual";
  return "other";
}

function normalizePhase(v?: string | null): string | null {
  if (!v) return null;
  const s = String(v).toLowerCase();
  if (s.includes("1")) return "Phase 1";
  if (s.includes("2")) return "Phase 2";
  if (s.includes("3")) return "Phase 3";
  if (s.includes("4")) return "Phase 4";
  return null;
}

function safeStorageFileName(name: string): string {
  const cleaned = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");

  return cleaned || "etmf-document";
}

export function AddActiveTrialDialog({
  open,
  onOpenChange,
  siteOrgId,
  trialSiteId,
  onTrialCreated,
}: AddActiveTrialDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("trial-info");
  const [showClaimDialog, setShowClaimDialog] = useState(false);

  // Step 1 — 15% human input
  const [form, setForm] = useState({ title: "", sponsor_name: "", protocol_number: "" });

  // Step 2 — eTMF upload state
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Result state
  const [creating, setCreating] = useState(false);
  const [createdTrial, setCreatedTrial] = useState<{ id: string; title: string } | null>(null);
  const [trialSiteRowId, setTrialSiteRowId] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string[]>([]);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);

  const reset = () => {
    setStep("trial-info");
    setForm({ title: "", sponsor_name: "", protocol_number: "" });
    setPendingDocs([]);
    setExtracting(false);
    setUploading(false);
    setCreating(false);
    setCreatedTrial(null);
    setTrialSiteRowId(null);
    setAiSummary([]);
    setAiWarnings([]);
  };

  const close = () => {
    onOpenChange(false);
    // Delay reset so the closing animation isn't jarring
    setTimeout(reset, 250);
  };

  // ---------- Step 1 -> Step 2: create the trial via SECURITY DEFINER RPC ----------
  const handleCreateTrial = async () => {
    if (!form.title.trim()) {
      toast({ title: "Title required", description: "Enter the trial title.", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase.rpc("create_site_active_trial", {
        p_site_org_id: siteOrgId,
        p_title: form.title.trim(),
        p_sponsor_name: form.sponsor_name.trim() || null,
        p_nct_id: form.protocol_number.trim() || null,
        p_protocol_number: form.protocol_number.trim() || null,
        p_trial_site_id: trialSiteId ?? null,
      } as any);
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.trial_id) throw new Error("Trial was not created.");
      setCreatedTrial({ id: row.trial_id, title: form.title.trim() });

      // Use the exact trial_sites row from the current site page when available.
      // Falling back by organization can select the wrong row if an org has multiple site records,
      // which then blocks private eTMF storage RLS.
      if (trialSiteId) {
        setTrialSiteRowId(trialSiteId);
      } else {
        const { data: siteRow } = await supabase
          .from("trial_sites")
          .select("id")
          .eq("organization_id", siteOrgId)
          .eq("status", "active")
          .limit(1)
          .maybeSingle();
        if (siteRow?.id) setTrialSiteRowId(siteRow.id);
      }

      queryClient.invalidateQueries({ queryKey: ["active-trials"] });
      queryClient.invalidateQueries({ queryKey: ["cro-trials"] });

      toast({ title: "Trial created", description: "Now upload the eTMF documents — the AI will fill in the rest." });
      setStep("upload-etmf");
    } catch (e: any) {
      logger.error("create_site_active_trial failed", e);
      toast({
        title: "Could not create trial",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  // ---------- Step 2: pick files + AI extract ----------
  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0 || !createdTrial) return;
    setExtracting(true);
    try {
      const existing = new Set(pendingDocs.map((d) => `${d.file.name}::${d.size}`));
      const newDocs: PendingDoc[] = [];
      const aiHits: string[] = [];
      for (const file of Array.from(files)) {
        const key = `${file.name}::${file.size}`;
        if (existing.has(key)) {
          toast({ title: "Duplicate skipped", description: `${file.name} is already queued.` });
          continue;
        }
        existing.add(key);
        let docType = guessDocType(file.name);

        // Try AI extraction — non-fatal
        try {
          const { text } = await extractDocumentText(file);
          if (text && text.length >= 200) {
            const ex = await extractTrialMetadataInChunks(supabase as any, text, file.name);
            if (ex) {
              if (ex.document_type_guess) docType = ex.document_type_guess;

              const saveResult = await persistTrialExtraction(supabase as any, createdTrial.id, ex);

              const bits = saveResult.summaryBits;
              if (bits.length) aiHits.push(`${file.name}: ${bits.join(", ")}`);
            }
          } else {
            throw new Error(`Only ${text?.length ?? 0} characters could be read. This may be a scanned PDF and needs OCR.`);
          }
        } catch (err: any) {
          logger.warn("AI extract skipped", err);
          setAiWarnings((prev) => [...prev, `${file.name}: ${err?.message ?? "AI extraction skipped"}`]);
        }

        newDocs.push({ file, size: file.size, doc_type_guess: docType, status: "queued" });
      }
      setPendingDocs((prev) => [...prev, ...newDocs]);
      if (aiHits.length) {
        setAiSummary((prev) => [...prev, ...aiHits]);
        toast({
          title: "AI filled the remaining 85%",
          description: aiHits[0],
        });
      }
    } finally {
      setExtracting(false);
    }
  };

  // ---------- Step 2: file uploads into eTMF ----------
  const handleFileEtmf = async () => {
    if (!createdTrial) return;
    if (pendingDocs.length === 0) {
      // Nothing to file — proceed to Done
      setStep("done");
      return;
    }
    if (!trialSiteRowId) {
      toast({
        title: "Site record missing",
        description: "eTMF filing needs an active trial_sites row for this site.",
        variant: "destructive",
      });
      return;
    }
    setUploading(true);

    const targets = pendingDocs
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => d.status !== "filed");

    let filed = pendingDocs.filter((d) => d.status === "filed").length;
    let failed = 0;

    for (const { d: doc, idx } of targets) {
      setPendingDocs((prev) => prev.map((x, i) => (i === idx ? { ...x, status: "uploading", error: undefined } : x)));
      const map = TMF_ZONE_MAP[doc.doc_type_guess] || TMF_ZONE_MAP.other;
      try {
        const path = `${trialSiteRowId}/${createdTrial.id}/${Date.now()}-${safeStorageFileName(doc.file.name)}`;
        const { error: upErr } = await supabase.storage
          .from("etmf-documents")
          .upload(path, doc.file, { upsert: false });
        if (upErr) throw upErr;
        const { error: insErr } = await supabase.from("etmf_documents").insert({
          trial_site_id: trialSiteRowId,
          document_category: map.category,
          document_type: doc.doc_type_guess,
          document_name: doc.file.name,
          file_url: path,
          status: "pending_review",
          tmf_zone: map.zone,
          tmf_section: map.section,
          tmf_artifact: map.artifact,
          auto_filed: true,
          source_module: "add_active_trial_dialog",
          source_record_id: createdTrial.id,
          source_system_doc_id: path,
          is_essential: true,
        } as any);
        if (insErr) {
          await supabase.storage.from("etmf-documents").remove([path]);
          throw insErr;
        }
        filed += 1;
        setPendingDocs((prev) => prev.map((x, i) => (i === idx ? { ...x, status: "filed" } : x)));
      } catch (e: any) {
        logger.error("eTMF filing failed", {
          fileName: doc.file.name,
          trialSiteRowId,
          trialId: createdTrial.id,
          error: e,
        });
        failed += 1;
        setPendingDocs((prev) =>
          prev.map((x, i) => (i === idx ? { ...x, status: "failed", error: e?.message ?? "Upload failed" } : x))
        );
      }
    }
    setUploading(false);

    if (failed === 0) {
      toast({
        title: `${filed} document${filed === 1 ? "" : "s"} filed into eTMF`,
        description: aiSummary.length
          ? "AI extracted protocol, visit and fee data is saved on the trial."
          : "Documents are filed. Run AI Protocol Extraction if fields are still empty.",
      });
      setStep("done");
    } else {
      toast({
        title: `${filed} filed, ${failed} failed`,
        description: "Failed rows stay in the list — fix and click File again to retry.",
        variant: "destructive",
      });
    }
  };

  const handleFinish = () => {
    if (createdTrial && onTrialCreated) {
      onTrialCreated(createdTrial.id, createdTrial.title);
    }
    close();
  };

  const skipEtmfForNow = () => {
    toast({
      title: "Trial saved",
      description: "You can upload eTMF documents later from the trial's eTMF tab.",
    });
    setStep("done");
  };

  const rollup = {
    total: pendingDocs.length,
    filed: pendingDocs.filter((d) => d.status === "filed").length,
    queued: pendingDocs.filter((d) => d.status === "queued").length,
    uploading: pendingDocs.filter((d) => d.status === "uploading").length,
    failed: pendingDocs.filter((d) => d.status === "failed").length,
  };

  const StepPill = ({ n, label, active, done }: { n: number; label: string; active: boolean; done: boolean }) => (
    <div className={`flex items-center gap-1.5 ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
      <div
        className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${
          done ? "bg-primary text-primary-foreground" : active ? "bg-primary/20 text-primary" : "bg-muted"
        }`}
      >
        {done ? <CheckCircle2 className="h-3 w-3" /> : n}
      </div>
      <span className="text-xs">{label}</span>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && (step === "upload-etmf" || step === "done")) {
          // Trial was already created — closing acts like Finish
          handleFinish();
          return;
        }
        onOpenChange(o);
        if (!o) setTimeout(reset, 250);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Active Trial</DialogTitle>
          <DialogDescription>
            Enter the basics — the AI fills the rest from your eTMF documents.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2 text-sm px-1 pb-2 border-b">
          <StepPill n={1} label="Trial info" active={step === "trial-info"} done={step !== "trial-info"} />
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <StepPill n={2} label="Upload eTMF" active={step === "upload-etmf"} done={step === "done"} />
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <StepPill n={3} label="Done" active={step === "done"} done={false} />
        </div>

        {/* STEP 1 — Trial info */}
        {step === "trial-info" && (
          <div className="space-y-4 pt-2">
            <div className="p-3 border rounded-lg bg-muted/40 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Have an existing trial?</p>
                <p className="text-xs text-muted-foreground">Search and claim it from the global database.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowClaimDialog(true)}>
                <Search className="h-4 w-4 mr-2" />
                Claim Trial
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="title">Trial Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="A Phase 3 Study of Drug X for Condition Y"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="sponsor">Sponsor</Label>
                  <Input
                    id="sponsor"
                    value={form.sponsor_name}
                    onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                    placeholder="Pfizer, GSK, ..."
                  />
                </div>
                <div>
                  <Label htmlFor="protocol">Protocol / NCT #</Label>
                  <Input
                    id="protocol"
                    value={form.protocol_number}
                    onChange={(e) => setForm({ ...form, protocol_number: e.target.value })}
                    placeholder="NCT12345678 or protocol number"
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-start gap-2 p-3 rounded-md border bg-primary/5">
                <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <p>
                  You only need these three fields. The AI reads your uploaded eTMF documents on the next step and fills in
                  phase, protocol version, target enrollment, visits, endpoints, dosing, I/E criteria, fee schedule and participant stipends.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleCreateTrial} disabled={creating || !form.title.trim()}>
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Next: Upload eTMF
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — eTMF upload */}
        {step === "upload-etmf" && createdTrial && (
          <div className="space-y-4 pt-2">
            <div className="p-3 border rounded-lg bg-primary/5 text-sm">
              <p className="font-medium">Trial created: {createdTrial.title}</p>
              <p className="text-xs text-muted-foreground">
                Drop your Protocol, Investigator's Brochure, ICF, Lab Manual, and IP Manual — one or many. Each file is
                filed into the correct DIA TMF zone; the AI extracts remaining metadata.
              </p>
            </div>

            <label
              htmlFor="etmf-files"
              className="flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent/40 transition"
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">Choose eTMF documents</p>
              <p className="text-xs text-muted-foreground">PDF / DOCX — multi-select supported</p>
              <input
                id="etmf-files"
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => handleFilesSelected(e.target.files)}
              />
            </label>

            {extracting && (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> AI reading document(s)…
              </p>
            )}

            {pendingDocs.length > 0 && (
              <>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Total {rollup.total}</Badge>
                  <Badge variant="secondary">Queued {rollup.queued}</Badge>
                  <Badge className="bg-blue-600 text-white">Uploading {rollup.uploading}</Badge>
                  <Badge className="bg-green-600 text-white">Filed {rollup.filed}</Badge>
                  {rollup.failed > 0 && <Badge variant="destructive">Failed {rollup.failed}</Badge>}
                </div>

                <div className="space-y-1 max-h-52 overflow-y-auto rounded-md border">
                  {pendingDocs.map((d, idx) => {
                    const map = TMF_ZONE_MAP[d.doc_type_guess] || TMF_ZONE_MAP.other;
                    return (
                      <div
                        key={`${d.file.name}-${idx}`}
                        className="flex items-center gap-2 px-3 py-2 text-xs border-b last:border-b-0"
                        title={d.error || undefined}
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{d.file.name}</p>
                          <p className="text-muted-foreground truncate">
                            → DIA {map.section} · {map.artifact}
                          </p>
                        {d.status === "failed" && d.error && (
                          <p className="mt-1 text-[11px] text-destructive line-clamp-2">{d.error}</p>
                        )}
                        </div>
                        {d.status === "queued" && <Badge variant="secondary">Queued</Badge>}
                        {d.status === "uploading" && (
                          <Badge className="bg-blue-600 text-white flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" /> Uploading
                          </Badge>
                        )}
                        {d.status === "filed" && (
                          <Badge className="bg-green-600 text-white flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Filed
                          </Badge>
                        )}
                        {d.status === "failed" && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Failed
                          </Badge>
                        )}
                        {d.status !== "uploading" && d.status !== "filed" && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => setPendingDocs((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {aiSummary.length > 0 && (
              <div className="rounded-md border bg-primary/5 p-3 text-xs space-y-1">
                <p className="font-medium flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI-populated</p>
                {aiSummary.map((s, i) => (
                  <p key={i} className="text-muted-foreground">• {s}</p>
                ))}
              </div>
            )}

            {aiWarnings.length > 0 && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs space-y-1">
                <p className="font-medium flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" /> AI needs review
                </p>
                {aiWarnings.map((s, i) => (
                  <p key={i} className="text-muted-foreground">• {s}</p>
                ))}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={skipEtmfForNow} disabled={uploading}>
                Skip eTMF for now
              </Button>
              <Button onClick={handleFileEtmf} disabled={uploading || extracting}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                File {pendingDocs.filter((d) => d.status !== "filed").length || ""} into eTMF
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Done */}
        {step === "done" && createdTrial && (
          <div className="space-y-4 pt-2">
            <div className="p-4 rounded-lg border bg-green-50 text-green-900 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Trial ready: {createdTrial.title}</p>
                <p className="text-xs">
                  {rollup.filed} eTMF document{rollup.filed === 1 ? "" : "s"} filed into DIA zones. You can add visits and
                  I/E criteria manually from the trial page if the AI missed anything.
                </p>
              </div>
            </div>

            {aiSummary.length > 0 && (
              <div className="rounded-md border p-3 text-xs space-y-1">
                <p className="font-medium">AI extracted</p>
                {aiSummary.map((s, i) => (
                  <p key={i} className="text-muted-foreground">• {s}</p>
                ))}
              </div>
            )}

            {aiWarnings.length > 0 && (
              <div className="rounded-md border p-3 text-xs space-y-1">
                <p className="font-medium">AI review needed</p>
                {aiWarnings.map((s, i) => (
                  <p key={i} className="text-muted-foreground">• {s}</p>
                ))}
              </div>
            )}

            <div className="text-xs text-muted-foreground flex items-start gap-2 p-3 rounded-md border">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>Next: configure protocol fees, then assign patients from the recruitment tab.</p>
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button onClick={handleFinish}>Finish</Button>
            </div>
          </div>
        )}
      </DialogContent>

      <ClaimTrialDialog
        open={showClaimDialog}
        onOpenChange={setShowClaimDialog}
        siteOrgId={siteOrgId}
        trialSiteId={trialSiteId}
        onTrialClaimed={(trialId) => {
          onTrialCreated?.(trialId, "Claimed Trial");
          close();
        }}
      />
    </Dialog>
  );
}
